import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash } from '@phosphor-icons/react';
import ApiErrorParser, { ApiErrorType } from 'api/ApiErrorParser';
import useFormError from 'api/hooks/useFormError';
import { useOrganization } from 'context/OrganizationContext';
import {
  ActivitiesService,
  ActivityTypeDefaultEnum,
  CancelablePromise,
  Contact,
  EggStateEnum,
  FollowUpMareCycleCheck,
  Horse,
  IsPregnantEnum,
  LocationEnum,
  MareCycleCheck,
  MarecyclecheckService,
  PregnancyCheckTermEnum,
  RealActivities,
  SexEnum,
  SoftnessEnum,
  UterusEnum,
} from 'openapi';
import { schemas } from 'openapi/zod-schemas';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button, { ButtonVariant } from 'ui/Button';
import { ErrorSection } from 'ui/Error';
import { CheckboxInput, DateInput, SelectInput, TextAreaInput, TextInput } from 'ui/Inputs';
import { OptionItemInterface } from 'ui/Inputs/SelectInput';
import { PageModal } from 'ui/Modals';
import { ActionProps, PageModalActions, PageModalContent, PageModalTitle } from 'ui/Modals/PageModal';
import {
  eggLocationEnumToString,
  eggStateEnumToString,
  pregnancyCheckIsPregnantEnumToString,
  pregnancyCheckTermEnumToString,
  softnessEnumToString,
  uterusEnumToString,
} from 'utilities/Breeding';
import { transformEmptyToUndefined, zodInputIsRequired } from 'utilities/zod';
import CystMap from '../CystMap';
import { useAccount } from 'context/AccountContext';
import { CalendarActivity, realActivityToDate } from 'utilities/Planning';
import { days, formatDate, today } from 'utilities/date.utilities';
import { addDays, format, sub } from 'date-fns';
import { z } from 'zod';
import RadioButtonGroup, { RadioButtonGroupOption } from 'ui/Inputs/RadioGroupInput';
import classNames from 'classnames';
import DropdownMenu from 'ui/DropdownMenu';
import { Alert } from 'ui/Alert';
import { Severity } from 'utilities/severity';
import { bloodlineString, formalHorseName } from 'utilities/Horse';
import useMareCycleBreedingPlan from './hooks/UseMareCycleBreedingPlan';
import HorseContactInputSelect from 'components/Common/HorseContactInputSelect';
import useMareCyclePreviousRecords from './hooks/UseMareCyclePreviousRecords';
import ContactInputSelect from 'components/Contacts/ContactInputSelect';
import useMareCycleFollowUp from './hooks/UseMareCycleFollowUp';
import useMareCycleStallion from './hooks/UseMareCycleStallion';
import HorseInputSelect from 'components/Horses/HorseInputSelect';
import { ApiPromises } from 'utilities/ApiPromises';
import { Label } from 'ui/Label';
import { predefinedApiRequestContacts } from 'utilities/ApiRequests';
import useMareCycleOwnerBadge from './hooks/UseMareCycleOwnerBadge';
import useMareCycleParentOrderItem from './hooks/UseMareCycleParentOrderItem';
import useMareCycleStartDateActivities from './hooks/UseMareCycleStartDateActivities';

interface Props {
  horses?: Horse[];
  contacts?: Contact[];
  skippable?: boolean;
  selectedMare?: Horse;
  mareCycleCheckActivity?: CalendarActivity;
  existingMareCycleCheck?: MareCycleCheck;
  open: boolean;
  onSaved: (reload: boolean, isSkipped: boolean, mareCycleCheck?: MareCycleCheck) => void;
  onRequestClose: () => void;
  onHorseCreated?: () => void;
}

// this is a copy of the MareCycleCheck but with some changes for the frontend
// this type is used to display the mare cycle check in the modal
// but just with a slightly change so we not saving the date but the days
export type AddFollowupMarecyclechecksDay = Pick<FollowUpMareCycleCheck, 'order_item_uid' | 'extra_info' | 'pregnancy_check_term'> & {
  days: number;
};

// Simplify the add_followup_insemination field of MareCycleCheck to a date.
// This is translated to a FollowUpInseminationCheck in the submit handler.
export interface ExtendedMareCycleCheck extends MareCycleCheck {
  add_insemination_days: string | undefined;
  add_mare_cycle_scan_days: string | undefined;
  add_mare_cycle_scan_days_custom: string | undefined;
  mare_customer: string | undefined;
  add_followup_marecyclechecks_days: AddFollowupMarecyclechecksDay[] | undefined;
}

// A mare cycle check is checks if the mare is ready for insemination.
export function SaveMareCycleCheck({
  horses,
  contacts: givenContacts,
  skippable,
  selectedMare,
  existingMareCycleCheck,
  mareCycleCheckActivity,
  open,
  onRequestClose,
  onSaved,
  onHorseCreated,
}: Props): JSX.Element {
  // Define the default value for the is_pregnant field
  // We should do it here as we are using some values in the watch subscription,
  // so it's not rerendered on the initial load,
  // but we still need this value in the form to make decisions
  const defaultIsPregnantValue: IsPregnantEnum =
    mareCycleCheckActivity &&
    mareCycleCheckActivity.activityType?.default === ActivityTypeDefaultEnum.MARE_CYCLE_CHECK &&
    mareCycleCheckActivity.pregnancyCheckTerm !== undefined
      ? IsPregnantEnum.YES
      : IsPregnantEnum.NO;

  const [contacts, setContacts] = useState<Contact[] | undefined>(givenContacts);
  const [relatedInseminationActivity, setRelatedInseminationActivity] = useState<RealActivities | undefined>(undefined);
  const [addInseminationDaysFormValue, setAddInseminationDaysFormValue] = useState<string | undefined>(undefined);
  const [isPregnantFormValue, setIsPregnantFormValue] = useState<IsPregnantEnum | undefined>(undefined);
  const [addMarecycleScanDaysFormValue, setAddMarecycleScanDaysFormValue] = useState<string | undefined>(undefined);

  const { t } = useTranslation();
  const { selectedOrganization, selectedOrganizationUid, selectedOrganizationDetails, generateCacheKey } = useOrganization();
  const { accountDetails, formatDate: formatDatePretty } = useAccount();

  // list only contact that are mareked as insemination station
  const inseminationStationContacts = (contacts ?? []).filter(contact => contact.is_insemination_station);

  const schema = useMemo(() => {
    return schemas.MareCycleCheck.extend({
      add_insemination_days: z.string().optional().nullable(),
      // only for the frontend needed
      mare_customer: z.string(),
      stallion_uid: addInseminationDaysFormValue ? z.string() : z.string().optional(),

      // plan a SCAN
      // this is a date and will be converted when submitting to a add_followup_marecyclechecks<FollowUpMareCycleCheck> with pregnancy_check_term = ''
      add_mare_cycle_scan_days: z.string().optional().nullable(),
      add_mare_cycle_scan_days_custom: addMarecycleScanDaysFormValue === '-1' ? z.number().min(1).max(300) : z.any().optional().nullable(),

      // WHen the mare is pregnant we need to set the uterus and cervix to required
      uterus: isPregnantFormValue === IsPregnantEnum.NO ? schemas.UterusEnum : schemas.UterusEnum.optional(),
      cervix: isPregnantFormValue === IsPregnantEnum.NO ? schemas.SoftnessEnum : schemas.SoftnessEnum.optional(),

      // a small copy of add_followup_marecyclechecks but with some changes
      // so we support days instead of date
      add_followup_marecyclechecks_days: z
        .array(
          z.object({
            days: z.number().min(1).max(300),
            order_item_uid: z.union([z.string(), z.null()]).optional(),
            pregnancy_check_term: schemas.PregnancyCheckTermEnum.optional(),
            extra_info: z.string().optional(),
          }),
        )
        .optional(),
    }).omit({
      uid: true,
      activity_uid: true,
      created_by: true,
      egg_set: true,
      cyst_set: true,
      add_followup_marecyclechecks: true,
      date: mareCycleCheckActivity ? true : undefined,
      mare_uid: true, // we can omit it, as we used it from the mare_customer field instead
      mare_customer: existingMareCycleCheck || selectedMare ? true : undefined, // only for the frontend needed
      add_mare_cycle_scan_days_custom: existingMareCycleCheck ? true : undefined, // omit it when we have a existing mare cycle check as this is only needed when creating a new one
      add_mare_cycle_scan_days: existingMareCycleCheck ? true : undefined, // omit it when we have a existing mare cycle check as this is only needed when creating a new one
      add_insemination_days: mareCycleCheckActivity ? undefined : true,
      add_followup_insemination: true,
      order_item_uid: true, // @TODO Optionally connect to the order.
      created_on: true,
      last_modified_on: true,
      // omit the pregnancy_check_term when we have a existing mare cycle check or when the mare is not pregnant
      pregnancy_check_term: isPregnantFormValue === IsPregnantEnum.NO ? true : undefined,
      breeding_plan: true,
      // uterus, cervix, uterus_has_fluid are only needed when the mare is pregnant
      uterus: isPregnantFormValue !== IsPregnantEnum.NO ? true : undefined,
      cervix: isPregnantFormValue !== IsPregnantEnum.NO ? true : undefined,
      uterus_has_fluid: isPregnantFormValue !== IsPregnantEnum.NO ? true : undefined,
    });
  }, [
    addInseminationDaysFormValue,
    addMarecycleScanDaysFormValue,
    existingMareCycleCheck,
    isPregnantFormValue,
    mareCycleCheckActivity,
    selectedMare,
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    watch,
    setValue,
  } = useForm<ExtendedMareCycleCheck>({
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
  });

  // Use a fieldArray for the egg items.
  const {
    fields: eggFields,
    append: appendEgg,
    remove: removeEgg,
  } = useFieldArray({
    name: 'egg_set',
    control,
  });

  // Use a fieldArray for the cyst items.
  const {
    fields: cystFields,
    append: appendCyst,
    remove: removeCyst,
    update: updateCyst,
  } = useFieldArray({
    name: 'cyst_set',
    control,
  });

  const {
    fields: followupFields,
    append: followupAppend,
    remove: followupRemove,
  } = useFieldArray({
    name: 'add_followup_marecyclechecks_days',
    control,
  });

  const { fieldError, nonFieldErrors, setApiError } = useFormError(schema, errors, ['add_followup_marecyclechecks_days']);

  const addMareCycleScanDaysCustomFormValue = watch('add_mare_cycle_scan_days_custom', '0');
  const addFollupMareCycleDaysFormValue = watch('add_followup_marecyclechecks_days');
  const stallionUidFormValue = watch('stallion_uid');

  const mareCustomerFormValue = watch('mare_customer');
  const [mareUidFormValue] = mareCustomerFormValue?.split('-') ?? [undefined, undefined];

  // list only the mares
  const mares = useMemo(() => {
    if (!horses) return [];
    return horses.filter(horse => horse.sex === SexEnum._2);
  }, [horses]);

  // small hook for the mare Owner badge
  const { ownerBadgeElement } = useMareCycleOwnerBadge({ contacts, mares, mareUidFormValue });

  const { breedingPlanOptions, selectedBreedingPlan } = useMareCycleBreedingPlan({
    selectedMareUid: mareUidFormValue,
    selectedMare,
    existingMareCycleCheck,
    setValue,
    watch,
    modalIsOpen: open,
    contacts,
  });

  const { infoElement: parentOrderInfoElement, parentOrder } = useMareCycleParentOrderItem({ selectedMareUid: mareUidFormValue, open });

  // this hook will check and return the start date for the activities
  // it will take the related insemination activity and the parent order in to account
  const { activityInfoMessage, startDate: activityStartDate } = useMareCycleStartDateActivities({
    watch,
    relatedInseminationActivity,
    parentOrder,
  });

  // hook that will load the previous records of the mare
  // and format this into a table that will be displayed in the modal
  const { element: previousRecordsTable, clear: clearPreviousRecords } = useMareCyclePreviousRecords({
    selectedMareUid: mareUidFormValue,
    selectedMare,
    modalIsOpen: open,
  });

  const { stallionOptions, stallionProductWarning } = useMareCycleStallion({
    modalIsOpen: open,
    horses,
    stallionUidFormValue,
    addInseminationDaysFormValue,
  });

  const onSubmit = async (data: ExtendedMareCycleCheck) => {
    if (!selectedOrganization) {
      console.error('Save mare cycle check without selected organization.');
      return;
    }

    if (data.add_insemination_days) {
      const days = Number(data.add_insemination_days);
      if (!isNaN(days)) {
        data.add_followup_insemination = {
          date: formatDate(addDays(data.date, days)),
          // TODO: The order_item_uid can be assigned to connect this MareCycleCheck to an Order.
          // order_item_uid?: 'id'
        };
      } else {
        console.error('Invalid insemination days', data);
      }

      // reset the old values so we dont send them to the backend
      data.add_insemination_days = undefined;
    }

    if (mareCycleCheckActivity) {
      data.activity_uid = mareCycleCheckActivity.uid;
      data.date = formatDate(mareCycleCheckActivity.startTime);
    }

    let promise: CancelablePromise<MareCycleCheck>;
    if (existingMareCycleCheck) {
      promise = MarecyclecheckService.marecyclecheckPartialUpdate({
        mareOrganisationUid: selectedOrganization.uid,
        uid: existingMareCycleCheck.uid,
        requestBody: data,
      });
    } else {
      // define the array for the marecyclechecks
      // it can be filled by both a scan and the followup days
      data.add_followup_marecyclechecks = [];

      // check if the user needs a scan activity
      // this is just the same as the followup activity but without the pregnancy_check_term
      if (data.add_mare_cycle_scan_days) {
        // check if we have a custom date
        let scanDays: string | undefined = data.add_mare_cycle_scan_days;
        if (scanDays === '-1' && data.add_mare_cycle_scan_days_custom) {
          scanDays = data.add_mare_cycle_scan_days_custom;
        }

        if (scanDays) {
          const days = Number(scanDays);
          if (!isNaN(days)) {
            data.add_followup_marecyclechecks.push({
              // use the date as start date here
              date: formatDate(addDays(data.date, days)),
              pregnancy_check_term: undefined,
              order_item_uid: relatedInseminationActivity?.order_item_uid,
            });
          } else {
            console.error('Invalid scan days', data);
          }
        }

        // reset the old values so we dont send them to the backend
        data.add_mare_cycle_scan_days = undefined;
        data.add_mare_cycle_scan_days_custom = undefined;
      }

      // convert the add_followup_marecyclechecks_days to the add_followup_marecyclechecks
      if (data.add_followup_marecyclechecks_days) {
        data.add_followup_marecyclechecks = [
          ...data.add_followup_marecyclechecks,
          ...data.add_followup_marecyclechecks_days.map(item => ({
            // use the date as start date here
            date: formatDate(addDays(data.date, item.days)),
            pregnancy_check_term: item.pregnancy_check_term,
            order_item_uid: item.order_item_uid,
            extra_info: item.extra_info,
          })),
        ];

        // reset the old values so we dont send them to the backend
        data.add_followup_marecyclechecks_days = undefined;
      }

      if (mareCycleCheckActivity && mareCycleCheckActivity.horseUid) {
        data.mare_uid = mareCycleCheckActivity.horseUid;
      } else if (selectedMare) {
        data.mare_uid = selectedMare.uid;
      } else if (data.mare_customer) {
        // get the mareUid from the mare_customer field
        // we could be sure that the value is not undefined, as we have a validation for it
        const [mareUid] = data.mare_customer?.split('-') ?? [''];
        data.mare_customer = undefined;
        data.mare_uid = mareUid;
      }

      // if there where no scan or followup days we need to remove the empty array
      if (data.add_followup_marecyclechecks.length === 0) {
        data.add_followup_marecyclechecks = undefined;
      }

      promise = MarecyclecheckService.marecyclecheckCreate({
        mareOrganisationUid: selectedOrganization.uid,
        requestBody: data,
      });
    }

    try {
      onSaved(true, false, await promise);
      onRequestClose();
    } catch (e) {
      setApiError(new ApiErrorParser<MareCycleCheck>(e));
    }
  };

  /**
   * Find the related insemination for the mare cycle check.
   * Also make sure we check if we can use the mareUid from the selectedMare of the mareUidFormValue.
   * If none can be used, bailout and return undefined.
   */
  const findRelatedInseminationActivity = useCallback(async (): Promise<RealActivities | undefined> => {
    let mareUid: string | undefined;
    if (selectedMare) {
      mareUid = selectedMare.uid;
    } else if (mareUidFormValue) {
      mareUid = mareUidFormValue;
    }

    if (!mareUid) {
      console.warn('Cannot find last insemination activity because no mare is selected');
      return;
    }

    // set the date to 100 days in the past
    const dtendGt = sub(new Date(), { days: 100 });

    const inseminationActivities = await ActivitiesService.activitiesList({
      activityTypeDefault: 'INSEMINATION',
      organisationUid: selectedOrganizationUid ?? '',
      lastDoneAndLastAndFirstFuturePerHorseAndActivityType: true,
      dtendGt: formatDate(dtendGt),
      horseUidIn: [mareUid],
    });

    // Filter for the lastDone activity.
    const foundInsemination = inseminationActivities.results.find(activity => realActivityToDate(activity) < new Date());

    return foundInsemination;
  }, [mareUidFormValue, selectedMare, selectedOrganizationUid]);

  const resetToDefaults = useCallback(() => {
    if (existingMareCycleCheck) {
      reset(existingMareCycleCheck);
    } else {
      if (mareCycleCheckActivity) {
        reset({
          date: formatDate(mareCycleCheckActivity.startTime),
          extra_info: mareCycleCheckActivity.extraInfo,
          // If the given activity is a mare cycle check, we set the default to YES.
          is_pregnant: defaultIsPregnantValue,
        });
      } else {
        reset({
          date: today(),
          is_pregnant: IsPregnantEnum.NO,
        });
      }
    }
  }, [defaultIsPregnantValue, existingMareCycleCheck, mareCycleCheckActivity, reset]);

  // Event that will be fired after the modal has been closed
  const resetForm = () => {
    setApiError(undefined);
    removeEgg(0); // Because the default value is {null, null} we need to manually trigger a remove.
    removeCyst(0); // Because the default value is {null, null} we need to manually trigger a remove.
    followupRemove(); // remove all the followups
    setAddInseminationDaysFormValue(undefined); // clear the state for the insemination days as this should be undefined from the start
    setAddMarecycleScanDaysFormValue(undefined);

    resetToDefaults();
    clearPreviousRecords();
  };

  const uterusEnumOptions = useMemo(() => {
    const options: OptionItemInterface[] = [];
    Object.values(UterusEnum).forEach(value => {
      options.push({ id: value, name: uterusEnumToString(value) });
    });
    return options;
  }, []);

  const softnessEnumOptions = useMemo(() => {
    return Object.values(SoftnessEnum).reduce<OptionItemInterface[]>((prevVal, currentVal) => {
      prevVal.push({ id: currentVal, name: softnessEnumToString(currentVal) });
      return prevVal;
    }, []);
  }, []);

  const eggLocationEnumOptions = useMemo(() => {
    return Object.values(LocationEnum).reduce<OptionItemInterface[]>((prevVal, currentVal) => {
      prevVal.push({ id: currentVal, name: eggLocationEnumToString(currentVal) });
      return prevVal;
    }, []);
  }, []);

  const eggStateEnumOptions = useMemo(() => {
    return Object.values(EggStateEnum).reduce<OptionItemInterface[]>((prevVal, currentVal) => {
      prevVal.push({ id: currentVal, name: eggStateEnumToString(currentVal) });
      return prevVal;
    }, []);
  }, []);

  /**
   * Generate a list of options for the insemination date.
   * We use the number of days as ID.
   */
  const planInseminationOptions = useMemo((): OptionItemInterface[] | undefined => {
    return [
      {
        id: 0,
        name: t('today', 'Today'),
      },
      {
        id: 1,
        name: t('tomorrow', 'Tomorrow'),
      },
      {
        id: 2,
        name: t('day-after-tomorrow', 'Day after tomorrow'),
      },
      {
        id: 3,
        name: t('three-days', 'Three days'),
      },
    ];
  }, [t]);

  /**
   * Generate a list of options for the Scan date.
   * We use the number of days as ID.
   */
  const planScanOptions = useMemo((): OptionItemInterface[] => {
    const options: OptionItemInterface[] = [
      {
        id: 0,
        name: t('today', 'Today'),
      },
      {
        id: 1,
        name: t('tomorrow', 'Tomorrow'),
      },
      {
        id: 2,
        name: t('day-after-tomorrow', 'Day after tomorrow'),
      },
      {
        id: 3,
        name: t('three-days', 'Three days'),
      },
    ];

    if (selectedOrganizationDetails?.days_between_insemination_and_pregnancy_check) {
      options.push({
        id: selectedOrganizationDetails.days_between_insemination_and_pregnancy_check,
        name: t('n-days', '{{number}} days', {
          number: selectedOrganizationDetails.days_between_insemination_and_pregnancy_check,
        }),
      });
    }

    options.push({
      id: '-1',
      name: t('custom', 'Custom'),
    });

    return options;
  }, [selectedOrganizationDetails, t]);

  /**
   * Hook that implement some logic for the followup of the mare cycle SCAN
   */
  const { showFollowUpElement, suggestDateForFollowup } = useMareCycleFollowUp({
    mareCycleCheckActivity,
    isPregnantFormValue,
    followupRemove,
    followupAppend,
    relatedInseminationActivity,
    watch,
  });

  /**
   * Return a formatted date for the given number of days.
   */
  const formInputDaysToDate = useCallback(
    (numberOfDays: number | string | undefined) => {
      if (numberOfDays === undefined) return;
      const nDay = Number(numberOfDays ?? '0');
      if (isNaN(nDay)) return;
      if (nDay > 300) return;
      if (nDay < 0) return;

      // we use the start date from either the last insemination, check of today
      const newDate = addDays(activityStartDate.date, nDay);
      const dayOfTheWeek = format(newDate, 'i');
      const daysList = days(t);
      const formattedDayOfTheWeek = daysList[Number(dayOfTheWeek) - 1];
      return `${formattedDayOfTheWeek} ${formatDatePretty(newDate)}`;
    },
    [activityStartDate, formatDatePretty, t],
  );

  const modalActions = useMemo((): ActionProps[] => {
    const array: ActionProps[] = [];

    if (skippable) {
      array.push({
        variant: ButtonVariant.Default,
        text: t('skip', 'Skip'),
        onClick: () => {
          onSaved(false, true);
          onRequestClose();
        },
      });
    }

    array.push({
      variant: ButtonVariant.Primary,
      text: t('save', 'Save'),
      type: 'submit',
      formId: 'addMareCycleCheck',
      loading: isSubmitting,
    });

    return array;
  }, [t, isSubmitting, skippable, onRequestClose, onSaved]);

  /* Load the Api data. Mainly the contacts if they are not already loaded.
   */
  const loadApiData = useCallback(() => {
    const promises = new ApiPromises();
    if (!selectedOrganizationUid) return promises;

    if (!contacts) {
      promises.appendListObj('all-contacts', setContacts, predefinedApiRequestContacts(selectedOrganizationUid, generateCacheKey));
    }

    return promises;
  }, [contacts, generateCacheKey, selectedOrganizationUid]);

  /**
   * Load the Api data
   */
  useEffect(() => {
    if (open) {
      const promise = loadApiData();
      promise.watchAll().catch(e => {
        const error = new ApiErrorParser(e);
        if (error.errorType !== ApiErrorType.CancelError) {
          console.warn('Failed to fetch ApiData for the mareCycleStallion.', e);
        }
      });

      return () => promise.cancel();
    }
  }, [loadApiData, open]);

  // Reset when the modal visibility is changed or when the existingCategory is changed.
  useEffect(() => {
    resetToDefaults();
  }, [reset, existingMareCycleCheck, open, selectedMare, resetToDefaults]);

  useEffect(() => {
    if (open && (selectedMare || mareUidFormValue)) {
      findRelatedInseminationActivity()
        .then(activity => setRelatedInseminationActivity(activity))
        .catch(e => {
          console.error('Failed to get related Insemination activity', e);
          setRelatedInseminationActivity(undefined);
        });
    } else {
      setRelatedInseminationActivity(undefined);
    }
  }, [open, findRelatedInseminationActivity, selectedMare, mareUidFormValue]);

  const pregnancyCheckIsPregnantEnumOptions = useMemo((): RadioButtonGroupOption[] => {
    return [
      { id: IsPregnantEnum.YES, name: pregnancyCheckIsPregnantEnumToString(IsPregnantEnum.YES) },
      { id: IsPregnantEnum.DUAL_PREGNANCY, name: pregnancyCheckIsPregnantEnumToString(IsPregnantEnum.DUAL_PREGNANCY) },
      { id: IsPregnantEnum.NO, name: pregnancyCheckIsPregnantEnumToString(IsPregnantEnum.NO) },
    ];
  }, []);

  const pregnancyCheckTermEnumOptions = useMemo(() => {
    return Object.values(PregnancyCheckTermEnum).reduce<OptionItemInterface[]>((prevVal, currentVal) => {
      prevVal.push({ id: currentVal, name: pregnancyCheckTermEnumToString(currentVal) });
      return prevVal;
    }, []);
  }, []);

  const followUpDropdownMenu = useMemo(() => {
    return [
      [
        {
          element: t('first-pregnancy-check', 'First pregnancy check'),
          onClick: () =>
            followupAppend({
              days: suggestDateForFollowup(PregnancyCheckTermEnum.FIRST),
              pregnancy_check_term: PregnancyCheckTermEnum.FIRST,
              order_item_uid: relatedInseminationActivity?.order_item_uid,
            }),
        },
        {
          element: t('second-pregnancy-check', 'Second pregnancy check'),
          onClick: () =>
            followupAppend({
              days: suggestDateForFollowup(PregnancyCheckTermEnum.SECOND),
              pregnancy_check_term: PregnancyCheckTermEnum.SECOND,
              order_item_uid: relatedInseminationActivity?.order_item_uid,
            }),
        },
        {
          element: t('third-pregnancy-check', 'Third pregnancy check'),
          onClick: () =>
            followupAppend({
              days: suggestDateForFollowup(PregnancyCheckTermEnum.THIRD),
              pregnancy_check_term: PregnancyCheckTermEnum.THIRD,
              order_item_uid: relatedInseminationActivity?.order_item_uid,
            }),
        },
      ],
    ];
  }, [followupAppend, relatedInseminationActivity?.order_item_uid, suggestDateForFollowup, t]);

  /**
   * Resets the insemination date when `isPregnantFormValue` changes.
   * This ensures the date is cleared when the pregnancy status changes,
   * as it should only be set when `is_pregnant` is NO.
   */
  useEffect(() => {
    if (isPregnantFormValue) {
      setValue('add_insemination_days', '');
    }
  }, [isPregnantFormValue, setValue]);

  /**
   * We use a watch subscription, so we can set a state value for our schema where normally the form value is not yet set
   */
  useEffect(() => {
    const subscription = watch(({ add_insemination_days, is_pregnant, add_mare_cycle_scan_days }, { name }) => {
      if (name === 'add_insemination_days') {
        setAddInseminationDaysFormValue(add_insemination_days);
      }

      if (name === 'is_pregnant') {
        setIsPregnantFormValue(is_pregnant);
      }

      if (name === 'add_mare_cycle_scan_days') {
        setAddMarecycleScanDaysFormValue(add_mare_cycle_scan_days);
      }
    });
    return () => subscription.unsubscribe();
  }, [setValue, watch]);

  /**
   * Make sure we set the value for the is_pregnant field to the default value
   * when the modal is opened.
   * This is needed because we use for is_pregnant the watch subscription, that has a downside to not set the value on initial load.
   */
  useEffect(() => {
    if (open) {
      setIsPregnantFormValue(defaultIsPregnantValue);
    }
  }, [defaultIsPregnantValue, open]);

  /**
   * If the user reset/clear the mare and we do not have a mare selected or existing mare cycle check
   * we need to reset the Requester/agent and stallion
   */
  useEffect(() => {
    if (mareUidFormValue === undefined && !selectedMare && !existingMareCycleCheck) {
      setValue('requester_uid', undefined);
      setValue('stallion_uid', undefined);
    }
  }, [existingMareCycleCheck, mareUidFormValue, selectedMare, setValue]);

  /**
   * Make sure we set the value for the pregnancy_check_term from the given activity
   */
  useEffect(() => {
    if (open && mareCycleCheckActivity) {
      setValue('pregnancy_check_term', mareCycleCheckActivity.pregnancyCheckTerm);
    }
  }, [mareCycleCheckActivity, open, setValue]);

  return (
    <PageModal
      open={open}
      onClosed={resetForm}
      parentElement='form'
      parentProps={{ id: 'addMareCycleCheck', noValidate: true, onSubmit: handleSubmit(onSubmit) }}
    >
      <PageModalTitle
        title={
          existingMareCycleCheck ? t('edit-mare-cycle-check', 'Edit mare cycle check') : t('new-mare-cycle-check', 'New mare cycle check')
        }
        onClose={() => onRequestClose()}
      />
      <PageModalContent>
        <ErrorSection className='mb-4' errors={nonFieldErrors} />

        {parentOrderInfoElement}

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
          <div className='flex flex-col gap-4 grow'>
            {!existingMareCycleCheck && !selectedMare ? (
              <div>
                <HorseContactInputSelect
                  label={t('mare', 'Mare')}
                  contacts={contacts ?? []}
                  horses={mares}
                  createMareOnly={true}
                  name='mare_customer'
                  control={control}
                  error={fieldError('mare_customer')}
                  createLabel={t('create-new-mare', 'Create new mare')}
                  onCreated={newHorse => {
                    // call in the onHorseCreated so it let the parent know to reload the horses
                    onHorseCreated?.();
                    // and set the form value so its selected
                    setValue('mare_customer', newHorse.uid);
                  }}
                />

                {/* below the input we place a badge with the owner(s) of the mare */}
                {/* if there are no owners, nothing is showed */}
                {ownerBadgeElement}
              </div>
            ) : (
              <div>
                <p className='block text-sm font-medium leading-4 text-gray-600 mb-2'>{t('mare', 'Mare')} *</p>
                {selectedMare && (
                  <>
                    <p>{formalHorseName(selectedMare)}</p>
                    <p className='text-gray-500 text-sm'>{bloodlineString(selectedMare)}</p>
                  </>
                )}
                {!selectedMare && <p>{t('unknown-mare', 'Unknown mare')}</p>}
              </div>
            )}

            {mareCycleCheckActivity && (
              <div>
                <p className='block text-sm font-medium leading-4 text-gray-600 mb-2'>{t('date', 'Date')} *</p>
                <p>{formatDatePretty(mareCycleCheckActivity.startTime)}</p>
              </div>
            )}

            <SelectInput
              disabled={!!existingMareCycleCheck}
              className={classNames({ hidden: breedingPlanOptions.length === 0 })}
              nullable={true}
              options={breedingPlanOptions}
              error={fieldError('breeding_plan_uid')}
              label={t('breeding-plan', 'Breeding plan')}
              nullableValue=''
              {...register('breeding_plan_uid', { setValueAs: transformEmptyToUndefined() })}
            />

            <div>
              <HorseInputSelect
                // disabled when we have an existing mare cycle check or a selected breeding plan with a stallion
                disabled={!!existingMareCycleCheck || (!!selectedBreedingPlan && !!selectedBreedingPlan.stallion_uid)}
                name='stallion_uid'
                label={t('stallion', 'Stallion')}
                required={zodInputIsRequired<ExtendedMareCycleCheck>(schema, 'stallion_uid')}
                control={control}
                horses={stallionOptions}
                error={fieldError('stallion_uid')}
              />
              {stallionProductWarning && <Alert className='mt-1' severity={Severity.Warning} message={stallionProductWarning} />}
            </div>

            <ContactInputSelect
              // disabled when we have an existing mare cycle check or a selected breeding plan with a requester
              disabled={!!existingMareCycleCheck || (!!selectedBreedingPlan && !!selectedBreedingPlan.requester_uid)}
              name='requester_uid'
              control={control}
              contacts={inseminationStationContacts}
              label={t('agent-or-insemination-station', 'Agent / Insemination-station')}
              error={fieldError('requester_uid')}
            />

            {previousRecordsTable}

            {!mareCycleCheckActivity && (
              <DateInput control={control} required={true} label={t('date', 'Date')} name='date' error={fieldError('date')} />
            )}

            <RadioButtonGroup<ExtendedMareCycleCheck>
              name='is_pregnant'
              required={true}
              control={control}
              options={pregnancyCheckIsPregnantEnumOptions}
              error={fieldError('is_pregnant')}
              label={t('is-pregnant', 'Is pregnant')}
            />
            {relatedInseminationActivity?.done_on && (
              <div>
                <p className='block text-sm font-medium leading-4 text-gray-600 mb-2'>
                  {t('related-insemination', 'Related insemination')}
                </p>
                <p>{formatDatePretty(realActivityToDate(relatedInseminationActivity))}</p>
              </div>
            )}

            {relatedInseminationActivity && !relatedInseminationActivity.done_on && (
              <div>
                <p className='block text-sm font-medium leading-4 text-gray-600 mb-2'>
                  {t('related-insemination', 'Related insemination')}
                </p>
                <Alert
                  className='text-sm'
                  severity={Severity.Warning}
                  message={t(
                    'found-related-insemination-not-done-warning',
                    'Found a related insemination at {{date}} but it is not marked as finished.',
                    { date: formatDatePretty(realActivityToDate(relatedInseminationActivity)) },
                  )}
                />
              </div>
            )}

            {isPregnantFormValue !== IsPregnantEnum.NO && (
              <SelectInput
                nullable={true}
                options={pregnancyCheckTermEnumOptions}
                error={fieldError('pregnancy_check_term')}
                label={t('pregnancy-check-term', 'Pregnancy check term')}
                {...register('pregnancy_check_term', { setValueAs: (val: unknown): unknown => (val === '----' ? undefined : val) })}
              />
            )}
            {isPregnantFormValue === IsPregnantEnum.NO && (
              <>
                <SelectInput
                  required={zodInputIsRequired<ExtendedMareCycleCheck>(schema, 'uterus')}
                  nullable={true}
                  options={uterusEnumOptions}
                  error={fieldError('uterus')}
                  label={t('uterus', 'Uterus')}
                  {...register('uterus', { setValueAs: (val: unknown): unknown => (val === '----' ? undefined : val) })}
                />
                <CheckboxInput
                  label={t('uterus_has_fluid', 'Uterus has fluid')}
                  {...register('uterus_has_fluid')}
                  error={fieldError('uterus_has_fluid')}
                />

                <SelectInput
                  required={zodInputIsRequired<ExtendedMareCycleCheck>(schema, 'cervix')}
                  nullable={true}
                  options={softnessEnumOptions}
                  error={fieldError('cervix')}
                  label={t('cervix', 'Cervix')}
                  {...register('cervix', { setValueAs: (val: unknown): unknown => (val === '----' ? undefined : val) })}
                />

                <div>
                  <Label className='mb-2'>{t('eggs', 'Eggs')}</Label>
                  <div className='p-2 rounded-lg border'>
                    {eggFields.length === 0 && <p className='text-gray-500 italic text-center py-4'>{t('no-eggs', 'No eggs')}</p>}
                    <table className='w-full'>
                      {eggFields.length > 0 && (
                        <thead>
                          <tr>
                            <td className='text-sm font-medium text-gray-600 pb-1'>{t('egg-location', 'Location')}</td>
                            <td className='text-sm font-medium text-gray-600 pl-1 pb-1'>{t('egg-size', 'Size')}</td>
                            <td className='text-sm font-medium text-gray-600 pl-1 pb-1'>{t('egg-hardness', 'Hardness')}</td>
                            <td className='text-sm font-medium text-gray-600 pl-1 pb-1'>{t('egg-state', 'State')}</td>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {eggFields.map((field, index) => (
                          <tr key={field.id} className='border-t'>
                            <td className='py-4' valign='top'>
                              <SelectInput
                                nullable={true}
                                options={eggLocationEnumOptions}
                                required={true}
                                nullableValue=''
                                error={fieldError('egg_set', { index, key: 'location' })}
                                {...register(`egg_set.${index}.location`, { setValueAs: transformEmptyToUndefined() })}
                              />
                            </td>
                            <td className='py-4 pl-1' valign='top'>
                              <TextInput
                                style={{ width: 50 }}
                                type='number'
                                lang={accountDetails?.language}
                                step='0.01'
                                postText={t('cm-metric', 'cm')}
                                error={fieldError('egg_set', { index, key: 'size_metric' })}
                                {...register(`egg_set.${index}.size_metric`, { setValueAs: transformEmptyToUndefined() })}
                              />
                            </td>
                            <td className='py-4 pl-1' valign='top'>
                              <SelectInput
                                nullable={true}
                                options={softnessEnumOptions}
                                required={true}
                                nullableValue=''
                                error={fieldError('egg_set', { index, key: 'hardness' })}
                                {...register(`egg_set.${index}.hardness`, { setValueAs: transformEmptyToUndefined() })}
                              />
                            </td>
                            <td className='py-4 pl-1' valign='top'>
                              <SelectInput
                                nullable={true}
                                options={eggStateEnumOptions}
                                required={true}
                                nullableValue=''
                                error={fieldError('egg_set', { index, key: 'egg_state' })}
                                {...register(`egg_set.${index}.egg_state`, { setValueAs: transformEmptyToUndefined() })}
                              />
                            </td>
                            <td className='py-4' valign='top'>
                              <button
                                type='button'
                                onClick={() => removeEgg(index)}
                                className='inline text-center cursor-pointer rounded hover:bg-red-700 hover:text-white px-1 py-1 focus:ring-0 focus:ring-offset-0'
                              >
                                <Trash className='inline' />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3} className='pt-2'>
                            <Button
                              icon={<Plus />}
                              type='button'
                              onClick={() => {
                                // Ignore ts error to have the newly added line show empty dropdown values.
                                // eslint-disable-next-line
                                // @ts-ignore
                                appendEgg({});
                              }}
                            >
                              {t('add-egg', 'Add egg')}
                            </Button>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </>
            )}

            <TextAreaInput
              label={t('note', 'Note')}
              error={fieldError('extra_info')}
              {...register('extra_info', { setValueAs: transformEmptyToUndefined() })}
            />
          </div>
          <div className='flex flex-col gap-4 grow'>
            {isPregnantFormValue === IsPregnantEnum.NO && (
              <div>
                <Label className='mb-2'>{t('cyst-map', 'Cyst map')}</Label>
                <CystMap cysts={cystFields} cystAdded={appendCyst} cystRemoved={removeCyst} cystUpdated={updateCyst} />
                <p className='text-xs text-red-500'>{fieldError('cyst_set')}</p>
              </div>
            )}
            {!existingMareCycleCheck && (
              <>
                {/* shows a small message when the start date is based on an previous insemination activity or order */}
                {activityInfoMessage}

                <SelectInput
                  // we hide the SCAN date when the mare is pregnant
                  className={classNames({ hidden: isPregnantFormValue !== IsPregnantEnum.NO })}
                  nullable={true}
                  options={planScanOptions}
                  error={fieldError('add_mare_cycle_scan_days')}
                  label={t('plan-scan', 'Plan scan')}
                  nullableLabel={t('no', 'No')}
                  nullableValue=''
                  hint={addMarecycleScanDaysFormValue !== '-1' ? (formInputDaysToDate(addMarecycleScanDaysFormValue) ?? '') : undefined}
                  {...register('add_mare_cycle_scan_days', {
                    setValueAs: transformEmptyToUndefined(),
                  })}
                />

                <TextInput
                  className={classNames({ '!hidden': addMarecycleScanDaysFormValue !== '-1' })}
                  error={fieldError('add_mare_cycle_scan_days_custom')}
                  type='number'
                  lang={accountDetails?.language}
                  step='1'
                  {...register('add_mare_cycle_scan_days_custom', { valueAsNumber: true })}
                  hint={formInputDaysToDate(addMareCycleScanDaysCustomFormValue)}
                  postText={t('days', 'days')}
                />
              </>
            )}
            {!existingMareCycleCheck && (
              <SelectInput
                // we hide the insemination date when the mare is pregnant
                className={classNames({ hidden: isPregnantFormValue !== IsPregnantEnum.NO })}
                nullable={true}
                options={planInseminationOptions}
                error={fieldError('add_insemination_days')}
                label={t('plan-insemination', 'Plan insemination')}
                nullableLabel={t('no', 'No')}
                nullableValue=''
                hint={formInputDaysToDate(addInseminationDaysFormValue)}
                {...register('add_insemination_days', {
                  setValueAs: transformEmptyToUndefined(),
                })}
              />
            )}
            {!existingMareCycleCheck && showFollowUpElement && (
              <div>
                <Label className='mb-2'>{t('plan-next-pregnancy-check', 'Followup pregnancy check(s)')}</Label>
                <div className={classNames('rounded-lg space-y-4', { 'border p-4 mb-2': followupFields.length > 0 })}>
                  {followupFields.map((field, index) => (
                    <div key={field.id}>
                      <p className='block text-sm font-medium leading-4 text-gray-600 mb-2'>
                        {field.pregnancy_check_term ? `${pregnancyCheckTermEnumToString(field.pregnancy_check_term)}` : 'unknown'}
                      </p>
                      <div className='flex gap-4 justify-center items-start'>
                        <TextInput
                          key={field.id}
                          className='grow'
                          error={fieldError('add_followup_marecyclechecks_days', { index, key: 'days' })}
                          type='number'
                          lang={accountDetails?.language}
                          step='1'
                          {...register(`add_followup_marecyclechecks_days.${index}.days`, { valueAsNumber: true })}
                          hint={`${relatedInseminationActivity ? t('from-last-insemination', 'from last insemination') + ' - ' : ''}${formInputDaysToDate(addFollupMareCycleDaysFormValue?.[index]?.days) ?? ''}`}
                          postText={t('days', 'days')}
                        />
                        <button
                          type='button'
                          onClick={() => followupRemove(index)}
                          className='inline rounded text-center cursor-pointer hover:bg-red-700 hover:text-white px-1 py-1 focus:ring-0 focus:ring-offset-0'
                        >
                          <Trash className='inline' />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className='flex'>
                    <DropdownMenu menuPlacement='bottom start' align='left' menuItems={followUpDropdownMenu}>
                      <Button icon={<Plus />} type='button'>
                        {followupFields.length === 0 ? t('add', 'Add') : t('add-another', 'Add another')}
                      </Button>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PageModalContent>
      <PageModalActions actions={modalActions} />
    </PageModal>
  );
}
